// Chemin: C:\gestion-copro\src\context\CoproprieteContext.tsx
// Session 37 : Contexte centralisé pour la copropriété active
// Session 54 : Ajout setCoproprieteId + lecture sessionStorage (portails copropriétés)
// Session 105 : DÉPLACÉ à l'intérieur du BrowserRouter (dans Router.tsx)
//               → Ajout lecture du paramètre URL ?copro= via useSearchParams
//               → Résout le bug multi-copropriétés Admin SaaS
//               → Quand l'Admin SaaS clique "Accéder" sur une copro, le ?copro=ID est lu ici

import React, { createContext, useContext, useEffect, useState, ReactNode, useCallback } from 'react';
import { useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import { doc, getDoc } from 'firebase/firestore';
import { db } from '../services/firebase';
import { useAuth } from './AuthContext';
import { Copropriete } from '../types/copropriete.types';

// ============================================
// TYPES DU CONTEXT
// ============================================

interface CoproprieteContextType {
  // Données
  coproprieteId: string | null;
  copropriete: Copropriete | null;
  
  // État
  loading: boolean;
  error: string | null;
  
  // Actions
  setCoproprieteId: (id: string | null) => void;
  refreshCopropriete: () => Promise<void>;
}

// ============================================
// CONTEXT
// ============================================

const CoproprieteContext = createContext<CoproprieteContextType | undefined>(undefined);

// ============================================
// PROVIDER
// ============================================

interface CoproprieteProviderProps {
  children: ReactNode;
}

export const CoproprieteProvider: React.FC<CoproprieteProviderProps> = ({ children }) => {
  const { userProfile, loading: authLoading } = useAuth();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const location = useLocation();
  
  const [coproprieteId, setCoproprieteIdState] = useState<string | null>(null);
  const [copropriete, setCopropriete] = useState<Copropriete | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Fonction pour charger une copropriété par son ID
  const loadCoproprieteById = useCallback(async (id: string) => {
    // Éviter de recharger si c'est déjà la copro active
    if (id === coproprieteId && copropriete) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);
    
    try {
      const docRef = doc(db, 'coproprietes', id);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        setCoproprieteIdState(id);
        setCopropriete({
          id: docSnap.id,
          ...docSnap.data()
        } as Copropriete);
      } else if (id.startsWith('vigie-')) {
        // Cas VIGIE : la copropriété virtuelle n'existe pas encore en Firestore
        // On construit un objet synthétique depuis vigieInfo du userProfile
        // pour que le contexte soit exploitable par toutes les pages VIGIE.
        // Note : userProfile peut ne pas être disponible ici (closure) — on
        // utilise un objet minimal suffisant pour le fonctionnement.
        setCoproprieteIdState(id);
        setCopropriete({
          id,
          nom: 'Ma copropriété',
          adresse: { rue: '', codePostal: '', ville: '' },
          nombreLots: 0,
          tantiemesTotal: 10000,
          typeConstruction: 'neuf_vefa',
          abonnement: {
            plan: 'essentiel',
            dateDebut: null as any,
            dateFin: null as any,
            statut: 'actif',
          },
          createdAt: null as any,
          updatedAt: null as any,
          createdBy: id.replace('vigie-', ''),
        } as Copropriete);
      } else {
        setError('Copropriété introuvable');
        setCoproprieteIdState(null);
        setCopropriete(null);
      }
    } catch (err) {
      console.error('Erreur chargement copropriété:', err);
      setError('Erreur lors du chargement de la copropriété');
    } finally {
      setLoading(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fonction exposée pour changer de copropriété
  const setCoproprieteId = useCallback((id: string | null) => {
    if (id) {
      loadCoproprieteById(id);
    } else {
      setCoproprieteIdState(null);
      setCopropriete(null);
    }
  }, [loadCoproprieteById]);

  // Fonction pour rafraîchir la copropriété courante
  const refreshCopropriete = useCallback(async () => {
    if (coproprieteId) {
      // Forcer le rechargement même si même ID
      setLoading(true);
      setError(null);
      try {
        const docRef = doc(db, 'coproprietes', coproprieteId);
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          setCopropriete({
            id: docSnap.id,
            ...docSnap.data()
          } as Copropriete);
        }
      } catch (err) {
        console.error('Erreur rafraîchissement copropriété:', err);
      } finally {
        setLoading(false);
      }
    }
  }, [coproprieteId]);

  // ============================================
  // SESSION 105 : Redirections automatiques
  // 1. Admin SaaS sur /admin sans ?copro= → /admin-saas
  // 2. Copro VIGIE sur /admin → /vigie
  // 3. Copro classique sur /vigie → /admin
  // ============================================

  useEffect(() => {
    if (loading || authLoading) return;
    
    const pathname = location.pathname;
    const coproParam = searchParams.get('copro');
    
    // Cas 1 : Admin SaaS arrive sur /admin sans ?copro= → retour cockpit
    if (
      userProfile?.isGestionnaireSaas && 
      pathname === '/admin' && 
      !coproParam
    ) {
      navigate('/admin-saas', { replace: true });
      return;
    }

    // Cas 2 & 3 : redirection vigie ↔ admin selon le type de copro
    if (!copropriete) return;
    
    const isVigieCopro = (copropriete as any).type === 'vigie' || copropriete.id?.startsWith('vigie-');
    
    if (isVigieCopro && pathname.startsWith('/admin') && !pathname.startsWith('/admin-saas')) {
      const vigieUrl = coproParam ? `/vigie?copro=${coproParam}` : '/vigie';
      navigate(vigieUrl, { replace: true });
    } else if (!isVigieCopro && pathname.startsWith('/vigie')) {
      const adminUrl = coproParam ? `/admin?copro=${coproParam}` : '/admin';
      navigate(adminUrl, { replace: true });
    }
  }, [copropriete, loading, authLoading, userProfile, location.pathname, searchParams, navigate]);

  // ============================================
  // SESSION 105 : Lecture du paramètre URL ?copro=
  // Quand l'URL change (ex: /admin?copro=xxx ou /vigie?copro=xxx),
  // on charge automatiquement la copropriété correspondante
  // ============================================
  
  const coproFromUrl = searchParams.get('copro');
  
  useEffect(() => {
    if (coproFromUrl && coproFromUrl !== coproprieteId) {
      loadCoproprieteById(coproFromUrl);
    }
  }, [coproFromUrl, coproprieteId, loadCoproprieteById]);

  // Effet principal : charger la copropriété au démarrage
  useEffect(() => {
    const loadInitialCopropriete = async () => {
      // Attendre que l'auth soit chargée
      if (authLoading) return;
      
      // Si pas connecté, reset
      if (!userProfile) {
        setCoproprieteIdState(null);
        setCopropriete(null);
        setLoading(false);
        return;
      }

      // PRIORITÉ 0 (Session 105) : Si ?copro= est dans l'URL, l'utiliser
      // L'effet useEffect ci-dessus s'en charge, mais on doit aussi
      // le gérer au chargement initial
      if (coproFromUrl) {
        await loadCoproprieteById(coproFromUrl);
        return;
      }

      // PRIORITÉ 1 : Vérifier si on vient d'un portail (sessionStorage)
      const pendingCoproprieteId = sessionStorage.getItem('pendingCoproprieteId');
      if (pendingCoproprieteId) {
        // Nettoyer le sessionStorage
        sessionStorage.removeItem('pendingCoproprieteId');
        sessionStorage.removeItem('pendingCoproprieteSlug');
        
        // Charger cette copropriété
        await loadCoproprieteById(pendingCoproprieteId);
        return;
      }

      // PRIORITÉ 2 : Cas VIGIE — coproprieteId synthétique vigie-{uid}
      // Le user VIGIE n'a pas de coproprieteIds — sa copropriété virtuelle
      // est identifiée par vigie-{uid} et stockée dans Firestore sous ce même ID.
      if (userProfile.role === 'vigie') {
        const vigieId = `vigie-${userProfile.id}`;
        await loadCoproprieteById(vigieId);
        return;
      }

      // PRIORITÉ 3 : Utiliser les copropriétés de l'utilisateur (CS / coordinateur)
      const ids = userProfile.coproprieteIds;
      
      if (!ids || ids.length === 0) {
        // Gestionnaire SaaS sans copropriété assignée = OK
        if (userProfile.isGestionnaireSaas) {
          setLoading(false);
          return;
        }
        setError('Aucune copropriété associée à votre compte');
        setLoading(false);
        return;
      }

      // Prendre la première copropriété par défaut
      await loadCoproprieteById(ids[0]);
    };

    loadInitialCopropriete();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userProfile, authLoading]);

  const value: CoproprieteContextType = {
    coproprieteId,
    copropriete,
    loading,
    error,
    setCoproprieteId,
    refreshCopropriete
  };

  return (
    <CoproprieteContext.Provider value={value}>
      {children}
    </CoproprieteContext.Provider>
  );
};

// ============================================
// HOOK
// ============================================

export const useCopropriete = (): CoproprieteContextType => {
  const context = useContext(CoproprieteContext);
  
  if (context === undefined) {
    throw new Error('useCopropriete doit être utilisé dans un CoproprieteProvider');
  }
  
  return context;
};

export default CoproprieteContext;
